import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { authenticate, AuthUser } from "./utils/auth";
import { api, submit } from "./utils/api";
import { closeWebApp, detectPlatform, getWebApp } from "./utils/platform";
import { runSubmit } from "./utils/submit-flow";
import { CategoryGrid } from "./components/CategoryGrid";
import { ActionList } from "./components/ActionList";
import { Landing } from "./components/Landing";
import { submitKindForPrompt } from "./utils/submit-kind";
import { CatalogResponse, SubmitPayload } from "./types";

export default function App() {
  // Из обычного браузера мини-апп открыть нельзя — авторизоваться нечем. Показываем витрину:
  // сюда ходит банк-эквайер проверять магазин, и сюда же ведёт кнопка «В магазин» с его формы.
  const [platform] = useState(detectPlatform);
  const isBrowser = platform === "browser";

  const [user, setUser] = useState<AuthUser | null>(null);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [screen, setScreen] = useState<"grid" | "inner">("grid");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const savedGridScrollY = useRef(0);

  useEffect(() => {
    if (isBrowser) {
      setLoading(false);
      return;
    }

    Promise.all([authenticate(), api.get<CatalogResponse>("/catalog").then((r) => r.data)])
      .then(([u, c]) => {
        setUser(u);
        setCatalog(c);
      })
      .catch((e) => setError(e.message || "Ошибка загрузки"))
      .finally(() => setLoading(false));
  }, [isBrowser]);

  // ActionList коротит body до viewport, window-скролл автоматически уезжает в 0.
  // Поэтому сохраняем позицию ДО ухода в inner, и явно восстанавливаем при возврате.
  useLayoutEffect(() => {
    if (screen === "inner") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    } else {
      window.scrollTo({ top: savedGridScrollY.current, left: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [screen, categoryId]);

  const handleOpenCategory = (id: string) => {
    if (!catalog?.categories.find((c) => c.id === id)) return;
    savedGridScrollY.current = window.scrollY;
    setCategoryId(id);
    setScreen("inner");
    getWebApp()?.HapticFeedback?.impactOccurred?.("soft");
  };

  const handleBack = () => {
    setScreen("grid");
    setCategoryId(null);
  };

  const handleSubmit = async (payload: SubmitPayload) => {
    if (submitting) return;
    setSubmitting(true);
    // Окно закрывается в ЛЮБОМ исходе (runSubmit → close в finally): TWA — одноразовый
    // пикер, а генерация уходит fire-and-forget. Раньше close() стоял только на успехе и
    // держался на одном хрупком вызове — при проглатывании клиентом окно висело навсегда.
    await runSubmit(payload, {
      submit,
      close: closeWebApp,
      onError: (e) => {
        // Страховка: если клиент почему-то не закрылся, снимаем спиннер — каталог снова
        // кликабелен и юзер повторит выбор. Полноэкранный экран ошибки больше не рисуем:
        // при обрыве на ответе генерация уже могла улететь, и «ошибка» вводила бы в заблуждение.
        console.error("[twa submit] отправка не удалась:", e);
        setSubmitting(false);
      },
    });
  };

  if (isBrowser) return <Landing />;

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <div style={{ color: "var(--color-sk-text-mute)" }}>Загрузка…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full flex items-center justify-center p-6">
        <div style={{ color: "var(--color-sk-accent)" }} className="text-center max-w-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!catalog) return null;

  if (screen === "inner" && categoryId) {
    const category = catalog.categories.find((c) => c.id === categoryId);
    if (!category) {
      handleBack();
      return null;
    }
    const actions = catalog.actions.filter((a) => a.category === categoryId);
    return (
      <ActionList
        category={category}
        actions={actions}
        onSelect={(actionId) => handleSubmit({ kind: "preset", actionId })}
        onCreate={(p) => handleSubmit({ kind: "create", prompt: p })}
        onBack={handleBack}
        busy={submitting}
      />
    );
  }

  return (
    <CategoryGrid
      categories={catalog.categories}
      balance={user?.paidBalance}
      hasPhoto={user?.hasPhoto ?? false}
      prompt={prompt}
      onPromptChange={setPrompt}
      onSubmitCustom={(p) => handleSubmit({ kind: submitKindForPrompt(user?.hasPhoto ?? false), prompt: p })}
      onOpenCategory={handleOpenCategory}
    />
  );
}
